/**
 * PREVIEW LAYOUT
 *
 * Resizable split layout with preview iframe (left) and AI chat (right).
 * Detects local vs cloud mode based on workspace type.
 * Uses PreviewChat in both modes; local mode streams through the preview store.
 */

import { useEffect, useMemo, useState } from "react";
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
} from "@/components/ui/resizable";
import { useWorkspaceFeatures } from "@/hooks/useWorkspaceFeatures";
import { useWorldStore } from "@/stores/worldStore";
import { initPreviewListeners } from "@/stores/localPreviewStore";
import { PreviewPanel } from "./PreviewPanel";
import { PreviewChat } from "./PreviewChat";

export function PreviewLayout() {
  const { isLocalWorkspace } = useWorkspaceFeatures();
  const projects = useWorldStore((s) => s.projects);

  const localProjects = useMemo(
    () => projects.filter((p) => p.localPath),
    [projects]
  );

  const [selectedProjectId, setSelectedProjectId] = useState<string>("");

  // Auto-select first local project
  useEffect(() => {
    if (localProjects.length > 0 && !localProjects.find((p) => p.id.toString() === selectedProjectId)) {
      setSelectedProjectId(localProjects[0].id.toString());
    }
  }, [localProjects, selectedProjectId]);

  const selectedProject = localProjects.find(
    (p) => p.id.toString() === selectedProjectId
  );

  // Initialize Tauri event listeners for local preview
  useEffect(() => {
    if (!isLocalWorkspace) return;
    const cleanup = initPreviewListeners();
    return cleanup;
  }, [isLocalWorkspace]);

  return (
    <ResizablePanelGroup direction="horizontal">
      <ResizablePanel defaultSize={70} minSize={40}>
        <PreviewPanel
          isLocalMode={isLocalWorkspace}
          selectedProject={selectedProject}
          localProjects={localProjects}
          onSelectProject={setSelectedProjectId}
        />
      </ResizablePanel>
      <ResizableHandle withHandle />
      <ResizablePanel defaultSize={30} minSize={20}>
        <PreviewChat
          isLocalMode={isLocalWorkspace}
          selectedProject={selectedProject}
        />
      </ResizablePanel>
    </ResizablePanelGroup>
  );
}
